import {useEffect} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {pipelinesClient} from '../../pipelines-api-integration/client';
import {onUpdatePipeline} from '../services/subscriptions';
import {OnUpdatePipelineSubscription} from '../types';
import {pipelinesKeys} from './usePipelinesQuery';

/**
 * Subscribes to backend pipeline update events via AppSync.
 * On each event, updates the cached detail record and invalidates list queries
 * so the table reflects the new status without manual refresh.
 */
export const useOnUpdatePipelineSubscription = () => {

    const queryClient = useQueryClient();

    useEffect(() => {
        const observable = pipelinesClient.graphql({query: onUpdatePipeline}) as {
            subscribe: (handlers: {
                next: (value: { data: OnUpdatePipelineSubscription }) => void;
                error: (err: unknown) => void;
            }) => { unsubscribe: () => void };
        };

        const subscription = observable.subscribe({
            next: ({data}) => {
                const pipeline = data?.onUpdatePipeline;
                if (!pipeline?.id) return;

                // Update the cached detail entry directly for instant UI update
                queryClient.setQueryData(pipelinesKeys.detail(pipeline.id), pipeline);

                // Invalidate lists so the table row updates
                queryClient.invalidateQueries({queryKey: pipelinesKeys.lists()});
            },
            error: (err) => {
                console.error('onUpdatePipeline subscription error', err);
            },
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [queryClient]);
};
